/**
 * Web Serial 传输：USB 直连飞控。
 *
 * 这是"台架调试与地面直连"的主力通道，也是唯一一条**不需要任何网络**的通道。
 * 三个必须处理好的现实问题：
 *
 *   1. `requestPort()` **必须在用户手势里调用** —— 浏览器不允许页面悄悄打开串口。
 *      因此同时支持"从已授权端口里挑一个"（`getPorts()`，免手势），
 *      这样刷新页面后可以自动重连到上次那根线。
 *   2. 同一个端口不能被两个标签页同时打开。第二个标签页会拿到一个语焉不详的错误，
 *      所以这里把它翻译成可读原因。
 *   3. 拔线时 `read()` 会抛错而不是正常结束，必须靠捕获异常来判定断开。
 */
import { BaseTransport, type TransportKind } from './transport';

export const COMMON_BAUD_RATES = [57_600, 115_200, 230_400, 460_800, 921_600] as const;

export const DEFAULT_BAUD_RATE = 115_200;

/** 常见飞控的 USB 供应商 ID（用于把设备选择器收窄到"可能是飞控"的范围） */
export const FLIGHT_CONTROLLER_USB_FILTERS = [
  { usbVendorId: 0x26ac }, // 3D Robotics / ArduPilot
  { usbVendorId: 0x2dae }, // CUAV
  { usbVendorId: 0x1209 }, // Generic / pid.codes
  { usbVendorId: 0x0483 }, // STMicroelectronics
  { usbVendorId: 0x3162 }, // Holybro
] as const;

export interface SerialTransportOptions {
  /** 已授权的端口（免手势路径） */
  readonly port?: SerialPort;
  readonly baudRate?: number;
  /** 需要用户选择端口时的候选过滤条件 */
  readonly filters?: readonly SerialPortFilter[];
  /** 注入串口实现，供测试使用 */
  readonly serial?: Serial;
}

function serialApi(): Serial | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { serial?: Serial }).serial ?? null;
}

export class SerialTransport extends BaseTransport {
  readonly kind: TransportKind = 'serial';

  private port: SerialPort | null;
  private readonly baudRate: number;
  private readonly filters: readonly SerialPortFilter[];
  private readonly injected: Serial | null;
  private readAbort: AbortController | null = null;
  private description = '串口';

  constructor(options: SerialTransportOptions = {}) {
    super();
    this.port = options.port ?? null;
    this.baudRate = options.baudRate ?? DEFAULT_BAUD_RATE;
    this.filters = options.filters ?? FLIGHT_CONTROLLER_USB_FILTERS;
    this.injected = options.serial ?? null;
  }

  get label(): string {
    return `${this.description} @ ${this.baudRate}`;
  }

  get baud(): number {
    return this.baudRate;
  }

  private api(): Serial | null {
    return this.injected ?? serialApi();
  }

  /**
   * 是否存在"免手势就能打开"的端口。
   * 界面据此决定是直接连，还是先弹一个"选择设备"的按钮（因为手势不能凭空造出来）。
   */
  async hasAuthorizedPort(): Promise<boolean> {
    const api = this.api();
    if (api === null) return false;
    try {
      const ports = await api.getPorts();
      return ports.length > 0;
    } catch {
      return false;
    }
  }

  /** 必须在用户手势中调用 */
  async requestPort(): Promise<SerialPort> {
    const api = this.api();
    if (api === null) {
      throw new Error('当前浏览器不支持 Web Serial。请使用桌面版 Chrome 或 Edge。');
    }
    const port =
      this.filters.length === 0
        ? await api.requestPort()
        : await api.requestPort({ filters: [...this.filters] });
    this.port = port;
    this.describe(port);
    return port;
  }

  private describe(port: SerialPort): void {
    const info = port.getInfo();
    const vendor = info.usbVendorId;
    const product = info.usbProductId;
    this.description =
      vendor === undefined
        ? '串口设备'
        : `${vendor.toString(16).padStart(4, '0')}:${(product ?? 0).toString(16).padStart(4, '0')}`;
  }

  override async open(): Promise<void> {
    const api = this.api();
    if (api === null) {
      const detail = '当前浏览器不支持 Web Serial。请使用桌面版 Chrome 或 Edge。';
      this.setState('error', detail);
      throw new Error(detail);
    }

    this.setState('opening');

    if (this.port === null) {
      const ports = await api.getPorts();
      const first = ports[0];
      if (first === undefined) {
        const detail =
          '没有已授权的串口。请点击"选择设备"并授权一次（浏览器要求由用户手势发起）。';
        this.setState('error', detail);
        throw new Error(detail);
      }
      this.port = first;
      this.describe(first);
    }

    const port = this.port;
    try {
      await port.open({
        baudRate: this.baudRate,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none',
        bufferSize: 4096,
      });
    } catch (error) {
      const detail = this.explainOpenFailure(error);
      this.setState('error', detail);
      throw new Error(detail);
    }

    this.setState('open');
    this.startReading(port);
  }

  private explainOpenFailure(error: unknown): string {
    const message = error instanceof Error ? error.message : String(error);
    // 同一个端口被别的标签页占了，浏览器给的是 "Failed to open serial port."
    if (/failed to open|access denied|in use/i.test(message)) {
      return '串口打不开：可能已被另一个标签页或另一个程序占用（同一端口同时只能由一个页面打开）。';
    }
    if (/networkerror|device.*disconnected/i.test(message)) {
      return '串口打不开：设备可能已被拔出。';
    }
    return `串口打不开：${message}`;
  }

  private startReading(port: SerialPort): void {
    const abort = new AbortController();
    this.readAbort = abort;

    void (async () => {
      try {
        while (port.readable !== null && !abort.signal.aborted) {
          const reader = port.readable.getReader();
          try {
            for (;;) {
              const { value, done } = await reader.read();
              if (done) break;
              if (value !== undefined) this.emitData(value);
            }
          } finally {
            reader.releaseLock();
          }
        }
      } catch (error) {
        if (abort.signal.aborted) return;
        // 拔线时 read() 会抛错，这是判定"断开"的唯一可靠信号
        const message = error instanceof Error ? error.message : String(error);
        this.setState('error', `串口连接中断（设备被拔出或端口被回收）：${message}`);
        return;
      }

      if (!abort.signal.aborted) {
        this.setState('error', '串口连接已结束（设备可能被拔出）。');
      }
    })();
  }

  override async close(): Promise<void> {
    this.readAbort?.abort();
    this.readAbort = null;

    const port = this.port;
    if (port !== null) {
      try {
        await port.close();
      } catch {
        /* 已经断开的端口再关会抛错，忽略 */
      }
    }
    this.setState('closed');
  }

  override async write(bytes: Uint8Array): Promise<void> {
    const port = this.port;
    if (port === null || port.writable === null) {
      throw new Error('串口未打开，无法发送');
    }
    const writer = port.writable.getWriter();
    try {
      await writer.write(bytes);
    } finally {
      writer.releaseLock();
    }
  }
}
